import type { DatePickerProps } from '@zillow/constellation';
import { DatePickerInput, type DatePickerInputProps } from '@zillow/constellation';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { displayDateFormat } from '../../constants/applicationsConstants';
import { DatePickerFix } from './DatePickerFix';
import { datePickerDropdownAttributes, getDatePickerInputProps } from './utils';

type FieldProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
};

export type DatePickerInputMemoizedProps = Omit<DatePickerInputProps, 'datepicker' | keyof FieldProps> &
  Pick<DatePickerProps, 'minDate' | 'maxDate'> &
  FieldProps;

/**
 * The constellation DatePickerInput has a bug where if the "datepicker" component is unstable between renders, the
 * popup will fail to layout correctly and expand the datepicker dropdown to full screen width.
 *
 * This fixes that bug by making the datepicker component stable by manually memoizing it.
 *
 * This still depends on the consumer to not pass unstable dates to minDate and maxDate, so don't
 * forget to not pass "new Date()", instead use dates that are stable across the whole day such as today()
 * or constant value dates such as new Date(2000, 1, 1).
 *
 * @param param0
 * @returns
 */
export const DatePickerInputMemoized = forwardRef<HTMLInputElement, DatePickerInputMemoizedProps>(
  (
    {
      value,
      onChange,
      onBlur,
      minDate: minDateUnstable,
      maxDate: maxDateUnstable,
      ...props
    }: DatePickerInputMemoizedProps,
    outterRef,
  ) => {
    // Return the same instance for dates with stable values:
    const minDate = useMemo(() => minDateUnstable, [minDateUnstable?.valueOf()]);
    const maxDate = useMemo(() => maxDateUnstable, [maxDateUnstable?.valueOf()]);

    const datePicker = useMemo(
      () => (
        <DatePickerFix
          {...datePickerDropdownAttributes}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={displayDateFormat}
        />
      ),
      [minDate, maxDate],
    );

    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(outterRef, () => innerRef.current!, []);
    return (
      <DatePickerInput
        ref={innerRef}
        datepicker={datePicker}
        // We adapt react-hook-forms "field" props to the DatePickerInput props
        // with our custom validations:
        {...getDatePickerInputProps({ value, onChange, onBlur })}
        {...props}
      />
    );
  },
);

DatePickerInputMemoized.displayName = 'DatePickerInputMemoized';
